/*
  variant-gallery.js

  Toont per variant alleen de foto's die in het variantmetaveld
  custom.variant_gallery staan, in de volgorde waarin ze daar staan. Kies je
  Green, dan zie je precies de foto's van Green en niets anders.

  De koppeling komt uit één JSON-blok dat de sectie meegeeft, in twee lagen:

    {"lijsten":[["123","124"],["125"]],"varianten":{"57171106070906":0,...}}

  "lijsten" bevat elke reeks media-id's één keer, "varianten" wijst per variant
  het nummer van zijn reeks aan. De foto's horen bij de kleur en niet bij de
  maat, dus alle maten van één kleur wijzen dezelfde reeks aan. Dat uitrekenen —
  inclusief het erven door een maat zonder eigen metaveld — gebeurt in Liquid;
  hier gebruiken we alleen de uitkomst.

  Het metaveld levert foto-objecten en de galerij werkt met media-objecten — die
  hebben elk hun eigen id. Dat naast elkaar leggen gebeurt ook in Liquid, waar
  allebei de objecten beschikbaar zijn.

  We halen niets uit de pagina en we verplaatsen geen knopen. Broadcast heeft
  een eigen laadstand voor afbeeldingen; een slide die je losknipt en er weer in
  hangt blijft wit. Verbergen met display:none doet dat niet, en de volgorde
  sturen we met de CSS-eigenschap order.

  Wat die volgorde betreft — nagemeten in theme.css:

  - .product__thumbs__holder is display:flex, dus order werkt daar en de
    duimnagels staan echt in de volgorde van het metaveld.
  - .product__slides is display:block (standaard), columns:2 (layout grid-1 en
    grid-2) of een grid waarin álle slides op grid-row-start:1 /
    grid-column-start:1 over elkaar liggen (layout thumbnails). In geen van die
    drie doet order iets: in de eerste twee wordt hij genegeerd, in de derde
    liggen de slides toch al op dezelfde plek. Daarom zetten we hem daar niet.
  - De pijltjes van het thema lopen via nextElementSibling en
    previousElementSibling, en dus via de DOM-volgorde; CSS order raakt dat
    niet. Wat we wél doen is voorkomen dat een pijltje op een verborgen slide
    uitkomt.
*/
(function () {
  'use strict';

  var READY = 'data-variant-gallery-ready';
  var ACTIVE_MEDIA = 'data-active-media';

  // Slides dragen het media-id rechtstreeks; duimnagels hebben het in de link
  // staan, met de sectiesleutel ervoor.
  function mediaIdOf(el) {
    var direct = el.getAttribute('data-image-id');
    if (direct) return direct;

    var holder = el.querySelector('[data-media-id]') || el;
    var combined = holder.getAttribute('data-media-id') || '';
    return combined.substring(combined.lastIndexOf('-') + 1);
  }

  // Video's en 3D-modellen kun je in het blok niet aanvinken — in het metaveld
  // komen alleen foto's. Ze vallen dus niet onder de lijst. Een video met een
  // kleur in zijn alt-tekst staat alleen bij die kleur (zie "videos" en
  // "kleurVan" in sections/product.liquid, 08-10-2026); een video zonder kleur
  // blijft bij elke kleur staan.
  function isImage(el) {
    var type = el.getAttribute('data-type') || el.getAttribute('data-media-type');
    return !type || type === 'image';
  }

  function show(el, visible) {
    if (visible) {
      el.style.removeProperty('display');
      el.removeAttribute('aria-hidden');
    } else {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
    }
  }

  function setup(root) {
    // shopify:section:load vuurt ook op een sectie die al draait. Zonder deze
    // stempel hangt er na elke herlading een luisteraar extra aan document, met
    // een lijst slides die niet meer in de pagina staat.
    if (root.hasAttribute(READY)) return;

    var scope = root.closest('[data-product]') || document;
    var holder = scope.querySelector('[data-variant-gallery-map]');
    var list = root.querySelector('[data-product-media-list]');
    if (!holder || !list) return;

    var map;
    try {
      map = JSON.parse(holder.textContent);
    } catch (error) {
      return;
    }

    var lists = map && map.lijsten;
    var byVariant = map && map.varianten;

    // Geen bruikbare kaart, of geen enkele variant die iets aanwijst: er valt
    // niets te filteren en niets te ordenen. Dan hangen we ook geen luisteraars
    // op en blijft de galerij precies zoals het thema hem neerzet.
    if (!Array.isArray(lists) || !byVariant) return;
    if (!Object.keys(byVariant).length) return;

    // Het reeksnummer van een variant opzoeken en er de media-id's bij halen.
    // Staat de variant niet in de kaart, of wijst hij een reeks aan die er niet
    // is, dan komt er een lege lijst uit en toont de galerij alles.
    function idsFor(variantId) {
      var place = byVariant[variantId];
      var ids = typeof place === 'number' ? lists[place] : null;
      return Array.isArray(ids) ? ids.map(String) : [];
    }

    // Alle foto's die een kleur via zijn lijst al opeist.
    var claimed = {};
    lists.forEach(function (l) {
      (l || []).forEach(function (id) { claimed[String(id)] = true; });
    });

    var videoKleur = (map && map.videos) || {};
    var kleurVan = (map && map.kleurVan) || {};
    var current = null;

    // Mag deze video bij de variant die nu gekozen is?
    function videoMag(el) {
      var k = videoKleur[String(mediaIdOf(el) || '')];
      return k === undefined || String(k) === String(kleurVan[current]);
    }

    var slides = Array.prototype.slice.call(list.querySelectorAll('[data-slide]'));
    if (slides.length < 2) return;

    var thumbHolder = root.querySelector('[data-thumbs-slider]');
    var thumbs = thumbHolder
      ? Array.prototype.slice.call(thumbHolder.querySelectorAll('[data-thumb-item]'))
      : [];

    root.setAttribute(READY, '');

    var allowed = null;
    var redirecting = false;

    function isAllowed(el) {
      if (!isImage(el)) return videoMag(el);
      if (!allowed) return true;
      return allowed.indexOf(String(mediaIdOf(el) || '')) !== -1;
    }

    // Zichtbaar maken wat mag, en de plek bepalen. Foto's krijgen de plek uit
    // het metaveld; video's en modellen gaan ervóór, in de volgorde waarin ze
    // in de pagina staan — een video staat altijd als eerste (zijn eis,
    // 08-10-2026).
    function arrange(items, ids, withOrder) {
      var keep = items.filter(function (el) {
        return isImage(el) ? ids.indexOf(String(mediaIdOf(el) || '')) !== -1 : videoMag(el);
      });

      // Niets herkend: dan liever alles dan een lege galerij.
      if (!keep.length) keep = items.slice();

      items.forEach(function (el, index) {
        show(el, keep.indexOf(el) !== -1);

        if (!withOrder) return;

        var place = isImage(el) ? ids.indexOf(String(mediaIdOf(el) || '')) : index - items.length;
        if (place === -1) place = ids.length + index;
        el.style.order = String(place);
      });

      return keep;
    }

    function clear(items, withOrder) {
      items.forEach(function (el) {
        show(el, true);
        if (withOrder) el.style.removeProperty('order');
      });
    }

    // Het thema zet zelf de actieve slide. Komt die op iets uit dat nu niet mag,
    // dan schuiven we door naar de eerstvolgende die wel mag — anders kijk je na
    // een klik op een pijltje tegen een lege galerij aan.
    function select(slide) {
      if (!slide) return;

      redirecting = true;
      slide.dispatchEvent(
        new CustomEvent('theme:media:select', {
          bubbles: true,
          detail: { id: slide.getAttribute('data-media-id') }
        })
      );
      window.requestAnimationFrame(function () {
        redirecting = false;
      });
    }

    function activeSlide() {
      return slides.filter(function (slide) {
        return slide.classList.contains('media--active');
      })[0];
    }

    // richting 1 = vooruit, -1 = terug.
    function nextAllowedAfter(slide, richting) {
      var start = slides.indexOf(slide);
      var n = slides.length;
      for (var step = 1; step <= n; step += 1) {
        var candidate = slides[((start + richting * step) % n + n) % n];
        if (isAllowed(candidate)) return candidate;
      }
      return null;
    }

    function apply(variantId) {
      if (!variantId) return;

      variantId = String(variantId);
      if (variantId === current) return;

      // Zelfde reeks als de vorige variant = zelfde kleur, andere maat. Op de
      // inhoud vergeleken, niet op het reeksnummer: de Liquid geeft maten van
      // één kleur soms elk een eigen nummer met dezelfde id's erin. Dan
      // verandert er niets aan de foto's en blijft de galerij op de pose
      // staan die je bekeek; anders sprong hij hieronder terug naar de video
      // (zijn wens 10-10-2026, zie brand-maat-pose.js).
      var vorige = current;
      current = variantId;
      if (vorige !== null) {
        var oud = idsFor(vorige).join(',');
        if (oud && oud === idsFor(variantId).join(',')) return;
      }

      var ids = idsFor(variantId);

      // Deze kleur heeft geen eigen lijst, maar een andere kleur wel: dan zijn
      // de foto's van deze kleur wat in geen enkele lijst staat, in de volgorde
      // van het product. Anders toonde de Classic Polo Shirt bij Grey ook de
      // vijf bruine foto's (09-10-2026). Staat alles al in een lijst, dan valt
      // hij terug op alles tonen, zoals het was.
      if (!ids.length) {
        ids = slides.filter(function (el) {
          return isImage(el) && !claimed[String(mediaIdOf(el) || '')];
        }).map(function (el) { return String(mediaIdOf(el) || ''); });
      }

      // Deze variant wijst niets aan: alles tonen, in de volgorde van het
      // product.
      if (!ids.length) {
        allowed = null;
        clear(slides, false);
        clear(thumbs, true);
        // Ook zonder lijst: een video van een andere kleur hoort er niet bij.
        slides.concat(thumbs).forEach(function (el) {
          if (!isImage(el) && !videoMag(el)) show(el, false);
        });
        return;
      }

      allowed = ids;

      var visible = arrange(slides, ids, false);
      if (thumbs.length) arrange(thumbs, ids, true);

      // Staat de galerij op een foto die nu niet meer mag, spring dan naar de
      // eerste foto uit het metaveld. Het thema zet de duimnagel er zelf bij
      // goed. Staat hij op een foto die wel mag, dan laten we hem met rust: bij
      // een variantwissel kiest het thema al de foto van die variant.
      // Heeft deze kleur een video, dan opent de galerij daarop: de video staat
      // altijd als eerste, ook als het thema bij de variantwissel al een foto
      // van die kleur koos.
      var active = activeSlide();
      var video = visible.filter(function (slide) { return !isImage(slide); })[0];
      if (video && active !== video) {
        // Na het thema: dat kiest bij een variantwissel zelf nog de foto van
        // de variant, en die keuze kwam anders over de onze heen.
        window.setTimeout(function () { select(video); }, 60);
      } else if (!active || visible.indexOf(active) === -1) {
        var first = visible.filter(function (slide) {
          return String(mediaIdOf(slide) || '') === ids[0];
        })[0];
        select(first || visible[0]);
      }

      // <product-images> meet zijn eigen hoogte en luistert daarvoor op
      // 'theme:resize:width', niet op een gewone resize van het venster. Op
      // mobiel staat er een overgang op die hoogte, dus blijft de galerij
      // anders staan op de maat van een foto die net verborgen is. Dit is
      // precies de gebeurtenis waar setHeight() en toggleEvents() aan hangen.
      window.requestAnimationFrame(function () {
        document.dispatchEvent(new CustomEvent('theme:resize:width'));
        window.dispatchEvent(new Event('resize'));
      });
    }

    document.addEventListener('theme:variant:change', function (event) {
      // Is deze galerij uit de pagina gehaald, dan hoeft er niets meer te
      // gebeuren; de opvolger luistert zelf al mee.
      if (!root.isConnected) return;
      if (!event.detail || !event.detail.variant) return;
      if (!event.target || !event.target.contains(root)) return;
      apply(event.detail.variant.id);
    });

    // De pijltjes van het thema lopen langs de slides in de DOM-volgorde en
    // slaan verborgen slides niet over. We kijken mee met de actieve slide en
    // schuiven door zodra die op iets verborgens uitkomt.
    //
    // In de richting waarin je ging. Tot 25-10-2026 schoof dit altijd vooruit,
    // en dan zat je bij "vorige" vast: van de eerste foto van een kleur (of van
    // de foto na een verborgen foto) kwam je op iets verborgens, en vooruit
    // doorschuiven bracht je terug op de foto waar je stond. Op mobiel betekende
    // dat: naar rechts vegen deed soms niets (zijn melding, Square Neck Midi
    // Dress). De richting volgt uit de vorige toegestane slide: wie één stap
    // terug zet, landt in de rondgang dichter achter hem dan ervoor.
    var laatste = activeSlide();
    new MutationObserver(function () {
      var active = activeSlide();
      if (active && isAllowed(active)) laatste = active;
      if (redirecting || !allowed || !root.isConnected) return;
      if (!active || isAllowed(active)) return;

      var n = slides.length;
      var afstand = laatste ? (slides.indexOf(active) - slides.indexOf(laatste) + n) % n : 1;
      select(nextAllowedAfter(active, afstand > n / 2 ? -1 : 1));
    }).observe(root, { attributes: true, attributeFilter: [ACTIVE_MEDIA] });

    var input = scope.querySelector('input[name="id"]');
    var fromUrl = new URLSearchParams(window.location.search).get('variant');

    apply(fromUrl || (input && input.value));
  }

  function init() {
    Array.prototype.slice
      .call(document.querySelectorAll('product-images'))
      .forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
